import React, { useState, useEffect, useCallback } from "react";
import { authService } from "../services/auth.service";
import { ownerSettingsService } from "../services/ownerSettings.service";
import {
  enrollmentConfigService,
  EnrollmentFormConfig,
} from "../services/enrollmentConfig.service";
import GuarantorSchemesSection from "../components/GuarantorSchemesSection";
import "../styles/SettingsPage.css";
import { getErrorMessage } from '../utils/error';

const CATEGORY_LABELS: Record<string, string> = {
  identity: "Identité",
  financial: "Financier",
  // `scheme` s'affichait tel quel, en anglais et en minuscules, au milieu de
  // libellés français. Absent de CATEGORY_OPTIONS à dessein : cette catégorie
  // est posée par le système pour l'attestation de garantie, pas choisie.
  scheme: "Garantie",
  other: "Autre",
};

const CATEGORY_OPTIONS = [
  { value: "identity", label: "Identité" },
  { value: "financial", label: "Financier" },
  { value: "other", label: "Autre" },
];

interface LocalConfig {
  id: string;
  documentName: string;
  category: string;
  isMandatoryCandidate: boolean;
  isMandatoryGuarantor: boolean;
  comment: string | null;
  // Track what changed vs server state
  dirty?: boolean;
}

const SettingsPage: React.FC = () => {
  const user = authService.getCurrentUser()?.user;

  // Document configs — server state
  const [serverConfigs, setServerConfigs] = useState<EnrollmentFormConfig[]>([]);
  // Document configs — local editable state
  const [localConfigs, setLocalConfigs] = useState<LocalConfig[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [configsLoading, setConfigsLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [configMessage, setConfigMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Add document modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDocName, setNewDocName] = useState("");
  const [newCategory, setNewCategory] = useState("other");
  const [newMandatoryCandidate, setNewMandatoryCandidate] = useState(true);
  const [newMandatoryGuarantor, setNewMandatoryGuarantor] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // Delete confirm
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Reset confirm
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Email templates
  const [acceptanceTemplate, setAcceptanceTemplate] = useState("");
  const [rejectionTemplate, setRejectionTemplate] = useState("");
  const [defaultAcceptancePlaceholder, setDefaultAcceptancePlaceholder] = useState("");
  const [defaultRejectionPlaceholder, setDefaultRejectionPlaceholder] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const toLocal = (configs: EnrollmentFormConfig[]): LocalConfig[] =>
    configs.map((c) => ({
      id: c.id,
      documentName: c.documentName,
      category: c.category,
      isMandatoryCandidate: c.isMandatoryCandidate,
      isMandatoryGuarantor: c.isMandatoryGuarantor,
      comment: c.comment,
    }));

  const loadConfigs = useCallback(async () => {
    if (!user?.id) return;
    setConfigsLoading(true);
    setConfigMessage(null);
    try {
      const userConfigs = await enrollmentConfigService.getConfigsForUser(user.id);
      if (userConfigs.length > 0) {
        setServerConfigs(userConfigs);
        setLocalConfigs(toLocal(userConfigs));
        setIsDefault(false);
      } else {
        const defaults = await enrollmentConfigService.getDefaults();
        setServerConfigs(defaults);
        setLocalConfigs(toLocal(defaults));
        setIsDefault(true);
      }
    } catch {
      setServerConfigs([]);
      setLocalConfigs([]);
      setIsDefault(true);
    } finally {
      setConfigsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadConfigs();
    ownerSettingsService
      .get()
      .then((settings) => {
        setAcceptanceTemplate(settings.acceptanceEmailTemplate || "");
        setRejectionTemplate(settings.rejectionEmailTemplate || "");
        setDefaultAcceptancePlaceholder(settings.defaultAcceptanceEmailTemplate);
        setDefaultRejectionPlaceholder(settings.defaultRejectionEmailTemplate);
      })
      .catch(() => {});
  }, [loadConfigs]);

  // Check if local configs differ from server configs
  const hasChanges = localConfigs.some((local) => {
    const server = serverConfigs.find((s) => s.id === local.id);
    if (!server) return false;
    return (
      local.category !== server.category ||
      local.isMandatoryCandidate !== server.isMandatoryCandidate ||
      local.isMandatoryGuarantor !== server.isMandatoryGuarantor
    );
  });

  const handleLocalToggle = (
    configId: string,
    field: "isMandatoryCandidate" | "isMandatoryGuarantor",
    value: boolean
  ) => {
    setLocalConfigs((prev) =>
      prev.map((c) => (c.id === configId ? { ...c, [field]: value } : c))
    );
  };

  const handleLocalCategoryChange = (configId: string, category: string) => {
    setLocalConfigs((prev) =>
      prev.map((c) => (c.id === configId ? { ...c, category } : c))
    );
  };

  const handleSaveConfigs = async () => {
    setSaveLoading(true);
    setConfigMessage(null);
    try {
      for (const local of localConfigs) {
        const server = serverConfigs.find((s) => s.id === local.id);
        if (!server) continue;
        const changed =
          local.category !== server.category ||
          local.isMandatoryCandidate !== server.isMandatoryCandidate ||
          local.isMandatoryGuarantor !== server.isMandatoryGuarantor;
        if (changed) {
          await enrollmentConfigService.updateConfig(local.id, {
            category: local.category,
            is_mandatory_candidate: local.isMandatoryCandidate,
            is_mandatory_guarantor: local.isMandatoryGuarantor,
          });
        }
      }
      setConfigMessage({ type: "success", text: "Configuration enregistrée avec succès" });
      await loadConfigs();
    } catch {
      setConfigMessage({ type: "error", text: "Erreur lors de la sauvegarde" });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDiscardChanges = () => {
    setLocalConfigs(toLocal(serverConfigs));
    setConfigMessage(null);
  };

  const handleCustomize = async () => {
    setConfigsLoading(true);
    try {
      for (const config of localConfigs) {
        await enrollmentConfigService.createConfig({
          document_name: config.documentName,
          is_mandatory_candidate: config.isMandatoryCandidate,
          is_mandatory_guarantor: config.isMandatoryGuarantor,
          category: config.category,
          comment: config.comment || undefined,
        });
      }
      await loadConfigs();
    } catch {
      // error handled by loadConfigs
    } finally {
      setConfigsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await enrollmentConfigService.deleteConfig(deleteId);
      setDeleteId(null);
      await loadConfigs();
    } catch {
      // ignore
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetToDefaults = async () => {
    setResetLoading(true);
    try {
      for (const config of serverConfigs) {
        await enrollmentConfigService.deleteConfig(config.id);
      }
      setShowResetConfirm(false);
      await loadConfigs();
    } catch {
      // ignore
    } finally {
      setResetLoading(false);
    }
  };

  const openAddModal = () => {
    setNewDocName("");
    setNewCategory("other");
    setNewMandatoryCandidate(true);
    setNewMandatoryGuarantor(false);
    setShowAddModal(true);
  };

  const handleAddDocument = async () => {
    const trimmed = newDocName.trim();
    if (!trimmed) return;
    setAddLoading(true);
    try {
      await enrollmentConfigService.createConfig({
        document_name: trimmed,
        is_mandatory_candidate: newMandatoryCandidate,
        is_mandatory_guarantor: newMandatoryGuarantor,
        category: newCategory,
      });
      setShowAddModal(false);
      await loadConfigs();
    } catch {
      // ignore
    } finally {
      setAddLoading(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsMessage(null);
    setSettingsLoading(true);
    try {
      await ownerSettingsService.update({
        acceptance_email_template: acceptanceTemplate || null,
        rejection_email_template: rejectionTemplate || null,
      });
      setSettingsMessage({ type: "success", text: "Paramètres enregistrés avec succès" });
    } catch (err) {
      const detail = getErrorMessage(err, "Erreur lors de la sauvegarde");
      setSettingsMessage({ type: "error", text: detail });
    } finally {
      setSettingsLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <h2>Paramètres</h2>

      {/* Section 1: Document configs */}
      <div className="settings-section">
        <div className="settings-section-header">
          <h3>Documents requis pour les candidatures</h3>
          {isDefault && localConfigs.length > 0 && (
            <span className="default-badge">Configuration par défaut</span>
          )}
        </div>
        <p className="settings-hint" style={{ marginBottom: "16px" }}>
          Cette configuration définit les documents demandés aux candidats.
        </p>
        {/* Sans cette précision, la colonne « Demander aussi au garant » se
            lisait comme la seule qui concerne le garant — alors que les pièces
            financières lui sont transférées automatiquement, en gardant
            l'obligation cochée dans la colonne « candidat ». */}
        <p className="settings-hint" style={{ marginBottom: "16px" }}>
          Quand un candidat déclare un garant, les documents de catégorie
          <strong> Financier </strong> lui sont demandés à lui plutôt qu&apos;au
          candidat. La colonne <strong>Demander aussi au garant</strong> sert aux
          pièces exigées des <em>deux</em> — la carte d&apos;identité, par exemple.
        </p>

        {configsLoading ? (
          <div className="settings-loading">Chargement...</div>
        ) : localConfigs.length === 0 ? (
          <div className="settings-empty">
            <p>Aucune configuration de documents trouvée.</p>
          </div>
        ) : (
          <>
            <table className="config-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Catégorie</th>
                  <th>Obligatoire candidat</th>
                  <th>Demander aussi au garant</th>
                  {!isDefault && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {localConfigs.map((config) => {
                  const server = serverConfigs.find((s) => s.id === config.id);
                  const rowChanged = server && (
                    config.category !== server.category ||
                    config.isMandatoryCandidate !== server.isMandatoryCandidate ||
                    config.isMandatoryGuarantor !== server.isMandatoryGuarantor
                  );
                  return (
                    <tr key={config.id} className={rowChanged ? "row-modified" : ""}>
                      <td>{config.documentName || "—"}</td>
                      <td>
                        {/* `scheme` est posée par le système et absente des
                            options : la liste retombait donc sur « Identité »,
                            et un simple enregistrement reclassait l'attestation
                            de garantie en pièce d'identité — elle disparaissait
                            alors de la branche « garantie institutionnelle »
                            pour être demandée à tout le monde. On l'affiche
                            comme une étiquette, non comme un choix. */}
                        {isDefault || config.category === "scheme" ? (
                          <span className={`category-badge category-${config.category}`}>
                            {CATEGORY_LABELS[config.category] || config.category}
                          </span>
                        ) : (
                          <select
                            className="category-select"
                            value={config.category}
                            onChange={(e) => handleLocalCategoryChange(config.id, e.target.value)}
                          >
                            {CATEGORY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          className="config-checkbox"
                          checked={config.isMandatoryCandidate}
                          disabled={isDefault}
                          onChange={(e) =>
                            handleLocalToggle(config.id, "isMandatoryCandidate", e.target.checked)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          className="config-checkbox"
                          checked={config.isMandatoryGuarantor}
                          disabled={isDefault}
                          onChange={(e) =>
                            handleLocalToggle(config.id, "isMandatoryGuarantor", e.target.checked)
                          }
                        />
                      </td>
                      {!isDefault && (
                        <td>
                          <button
                            className="delete-config-btn"
                            onClick={() => setDeleteId(config.id)}
                            title={localConfigs.length <= 1 ? "Au moins un document requis" : "Supprimer"}
                            disabled={localConfigs.length <= 1}
                          >
                            Supprimer
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {configMessage && (
              <div className={`form-message ${configMessage.type}`}>
                {configMessage.text}
              </div>
            )}

            <div className="config-actions">
              {isDefault ? (
                <button className="submit-button" onClick={handleCustomize}>
                  Personnaliser
                </button>
              ) : (
                <>
                  {hasChanges && (
                    <>
                      <button
                        className="submit-button"
                        onClick={handleSaveConfigs}
                        disabled={saveLoading}
                      >
                        {saveLoading ? "Enregistrement..." : "Enregistrer les modifications"}
                      </button>
                      <button className="cancel-button" onClick={handleDiscardChanges}>
                        Annuler
                      </button>
                    </>
                  )}
                  <button className="add-button" onClick={openAddModal}>
                    Ajouter un document
                  </button>
                  <button
                    className="reset-button"
                    onClick={() => setShowResetConfirm(true)}
                  >
                    Revenir aux valeurs par défaut
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* Section 2: Email templates */}
      <div className="settings-section">
        <h3>Modèles d'emails candidatures</h3>
        <p className="settings-hint">
          Personnalisez les emails envoyés aux candidats. Placeholders disponibles :{" "}
          {"{candidat_prenom}"}, {"{candidat_nom}"}, {"{chambre_nom}"},{" "}
          {"{coloc_nom}"}. Votre texte part tel quel dans un email habillé aux
          couleurs de coloc.app : les retours à la ligne sont conservés, les
          liens deviennent cliquables, et le HTML n'est pas interprété.
        </p>
        <form onSubmit={handleSaveSettings} className="email-form">
          <div className="form-group">
            <label htmlFor="acceptanceTemplate">Email d'acceptation</label>
            <textarea
              id="acceptanceTemplate"
              value={acceptanceTemplate}
              onChange={(e) => setAcceptanceTemplate(e.target.value)}
              rows={5}
              placeholder={defaultAcceptancePlaceholder || "Laissez vide pour utiliser le modèle par défaut"}
              className="template-textarea"
            />
          </div>
          <div className="form-group">
            <label htmlFor="rejectionTemplate">Email de rejet</label>
            <textarea
              id="rejectionTemplate"
              value={rejectionTemplate}
              onChange={(e) => setRejectionTemplate(e.target.value)}
              rows={5}
              placeholder={defaultRejectionPlaceholder || "Laissez vide pour utiliser le modèle par défaut"}
              className="template-textarea"
            />
          </div>
          {settingsMessage && (
            <div className={`form-message ${settingsMessage.type}`}>
              {settingsMessage.text}
            </div>
          )}
          <button type="submit" className="submit-button" disabled={settingsLoading}>
            {settingsLoading ? "Enregistrement..." : "Enregistrer les modèles"}
          </button>
        </form>
      </div>


      {/* Section 3: Accepted institutional guarantor schemes */}
      <GuarantorSchemesSection />

      {/* Add document modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Ajouter un document requis</h3>
            <div className="form-group">
              <label>Nom du document</label>
              <input
                type="text"
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                className="form-input"
                placeholder="Ex : Attestation d'assurance habitation"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Catégorie</label>
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="form-select"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group-row">
              <label>
                <input
                  type="checkbox"
                  checked={newMandatoryCandidate}
                  onChange={(e) => setNewMandatoryCandidate(e.target.checked)}
                />{" "}
                Obligatoire candidat
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={newMandatoryGuarantor}
                  onChange={(e) => setNewMandatoryGuarantor(e.target.checked)}
                />{" "}
                Demander aussi au garant
              </label>
            </div>
            <div className="modal-actions">
              <button
                className="submit-button"
                onClick={handleAddDocument}
                disabled={!newDocName.trim() || addLoading}
              >
                {addLoading ? "Ajout..." : "Ajouter"}
              </button>
              <button
                className="cancel-button"
                onClick={() => setShowAddModal(false)}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmer la suppression</h3>
            <p>Voulez-vous vraiment supprimer ce document de la configuration ?</p>
            <div className="modal-actions">
              <button
                className="delete-confirm-btn"
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Suppression..." : "Supprimer"}
              </button>
              <button className="cancel-button" onClick={() => setDeleteId(null)}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset confirm modal */}
      {showResetConfirm && (
        <div className="modal-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Revenir aux valeurs par défaut</h3>
            <p>
              Votre configuration personnalisée sera supprimée et remplacée par la
              configuration par défaut. Continuer ?
            </p>
            <div className="modal-actions">
              <button
                className="delete-confirm-btn"
                onClick={handleResetToDefaults}
                disabled={resetLoading}
              >
                {resetLoading ? "Réinitialisation..." : "Confirmer"}
              </button>
              <button
                className="cancel-button"
                onClick={() => setShowResetConfirm(false)}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
